import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import {
  TixIsValidResetPasswordTokenGQL,
  TixResetPasswordGQL
} from '@tix/data-access';
import { of, Subscription } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'tix-reset-password',
  templateUrl: './reset-password.component.html'
})
export class TixResetPasswordComponent implements OnInit, OnDestroy {
  isInvalidToken = false;
  isLoading = false;
  isResetting = false;
  isSuccess = false;

  resetPasswordToken?: string;

  private sub?: Subscription;
  constructor(
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    private resetPasswordMutation: TixResetPasswordGQL,
    private isValidTokenQuery: TixIsValidResetPasswordTokenGQL,
    private cd: ChangeDetectorRef
  ) {}

  resetForm = new FormGroup(
    {
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(256)
      ]),
      confirmPassword: new FormControl('', Validators.required)
    },
    {
      validators: [this.ConfirmedValidator('newPassword', 'confirmPassword')]
    }
  );

  get newPasswordControl() {
    return this.resetForm.controls['newPassword'] as FormControl;
  }

  get confirmPasswordControl() {
    return this.resetForm.controls['confirmPassword'] as FormControl;
  }

  ngOnInit(): void {
    this.sub = this.route.queryParams
      .pipe(
        take(1),
        mergeMap(queryParams => {
          this.resetPasswordToken = queryParams['token'];

          this.isLoading = true;
          return this.isValidTokenQuery
            .fetch(
              {
                token: this.resetPasswordToken as string
              },
              {
                fetchPolicy: 'no-cache'
              }
            )
            .pipe(
              tap(res => {
                this.isLoading = false;
                this.isInvalidToken = !res.data.isValidToken;
                this.cd.detectChanges();
              }),
              catchError(err => {
                console.error(err);
                this.snackbar.open('Something went wrong!');

                return of(null);
              })
            );
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (this.resetForm.invalid || this.resetForm.pristine) return;

    this.isResetting = true;

    this.resetPasswordMutation
      .mutate({
        newPassword: this.resetForm.value.newPassword,
        token: this.resetPasswordToken as string
      })
      .pipe(
        tap(() => {
          this.isSuccess = true;
          this.isResetting = false;
          this.cd.detectChanges();
        }),
        catchError(err => {
          this.isResetting = false;

          console.error(err);
          this.snackbar.open('Something went wrong!');

          this.cd.detectChanges();
          return of(null);
        })
      )
      .subscribe();

    // if (this.sub) this.sub.add(curSub);
    // else this.sub = curSub;
  }

  ConfirmedValidator(
    controlName: string,
    matchingControlName: string
  ): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const form = formGroup as FormGroup;
      const control = form.controls[controlName];
      const matchingControl = form.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmedValidator
      ) {
        return null;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmedValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
      return null;
    };
  }
}
